import React from 'react';
import { Props as PortfolioProps } from '~components/shared/Portfolio';

interface PortfolioNode {
  nodes: {
    id: number;
    acf: PortfolioProps;
    location: {
      name: string;
      id: number;
    }[];
    responsibility: {
      name: string;
      id: number;
    };
  }[];
}

interface PortfolioContextProviderProps {
  children: any;
  nodes: PortfolioNode[];
}

interface PortfolioContextProviderState {
  allPortfolios: PortfolioNode[];
  allFilteredPortfolios: PortfolioNode[];
  visiblePortfolios: PortfolioNode[];
  locations: string[];
  responsibilities: string[];
  hiddenContent: boolean;
  locationFilter: string;
  responsibilityFilter: string;
}

export const PortfolioContext = React.createContext({
  portfolios: [],
  locations: [],
  responsibilities: [],
  hiddenContent: false,
  showContent: null,
  setLocationFilter: null,
  setResponsibilityFilter: null,
  resetLocationFilter: null,
  resetResponsibilityFilter: null,
});

export class PortfolioContextProvider extends React.Component<
  PortfolioContextProviderProps,
  PortfolioContextProviderState
> {
  state = {
    hiddenContent: false,
    locations: [],
    responsibilities: [],
    allFilteredPortfolios: [],
    visiblePortfolios: [],
    allPortfolios: [],
    locationFilter: '',
    responsibilityFilter: '',
  };

  limitValue = 8;
  visiblePortfoliosLimit = this.limitValue;
  resetVisiblePortfoliosLimit = () => {
    this.visiblePortfoliosLimit = this.limitValue;
  };

  increaseViliblePortfoliosLimit = () => {
    this.visiblePortfoliosLimit += this.limitValue;
  };

  componentDidMount() {
    const { nodes } = this.props;
    this.setVisible(nodes);
    this.collectAllLocations(nodes);
    this.collectAllResponsibilities(nodes);

    this.setState({ allPortfolios: nodes });
  }

  setVisible = nodes => {
    const hiddenContent = nodes.length > this.visiblePortfoliosLimit;
    const visiblePortfolios = nodes.slice(0, this.visiblePortfoliosLimit);
    this.setState({ visiblePortfolios, hiddenContent });
  };

  showContent = () => {
    this.increaseViliblePortfoliosLimit();

    const {
      locationFilter,
      responsibilityFilter,
      allFilteredPortfolios,
      allPortfolios,
    } = this.state;
    if (locationFilter || responsibilityFilter) {
      this.setVisible(allFilteredPortfolios);
    } else {
      this.setVisible(allPortfolios);
    }
  };

  collectAllLocations = nodes => {
    const locations = [];
    nodes.map(node => {
      node.location.forEach(location => {
        if (!locations.includes(location.name)) {
          locations.push(location.name);
        }
      });
    });
    this.setState({ locations });
  };

  collectAllResponsibilities = nodes => {
    const responsibilities = [];
    nodes.map(node => {
      node.responsibility.forEach(responsibility => {
        if (!responsibilities.includes(responsibility.name)) {
          responsibilities.push(responsibility.name);
        }
      });
    });
    this.setState({ responsibilities });
  };

  filter = (value, portfolios, type: 'responsibility' | 'location') => {
    const filteredPortfolios = [];

    portfolios.forEach(portfolio => {
      const array =
        type === 'responsibility'
          ? portfolio.responsibility
          : portfolio.location;

      array.forEach(option => {
        if (option.name === value) {
          filteredPortfolios.push(portfolio);
        }
      });
    });

    return filteredPortfolios;
  };

  // Set filters
  setFilter = nodes => {
    this.resetVisiblePortfoliosLimit();
    this.setVisible(nodes);
    this.setState({
      allFilteredPortfolios: nodes,
    });
  };

  setResponsibilityFilter = value => {
    const { allPortfolios, locationFilter } = this.state;
    let portfolios;

    if (locationFilter) {
      portfolios = this.filter(locationFilter, allPortfolios, 'location');
    } else {
      portfolios = allPortfolios;
    }

    const allFilteredPortfolios = this.filter(
      value,
      portfolios,
      'responsibility'
    );
    this.setState({ responsibilityFilter: value });
    this.setFilter(allFilteredPortfolios);
  };

  setLocationFilter = value => {
    const { allPortfolios, responsibilityFilter } = this.state;

    let portfolios;
    if (responsibilityFilter) {
      portfolios = this.filter(
        responsibilityFilter,
        allPortfolios,
        'responsibility'
      );
    } else {
      portfolios = allPortfolios;
    }

    const allFilteredPortfolios = this.filter(value, portfolios, 'location');
    this.setState({ locationFilter: value });
    this.setFilter(allFilteredPortfolios);
  };

  // Reset filters
  resetFilter = nodes => {
    this.resetVisiblePortfoliosLimit();
    this.setVisible(nodes);
  };

  resetLocationFilter = () => {
    const { responsibilityFilter, allPortfolios } = this.state;

    let portfolios;
    if (responsibilityFilter) {
      portfolios = this.filter(
        responsibilityFilter,
        allPortfolios,
        'responsibility'
      );
    } else {
      portfolios = allPortfolios;
    }
    this.setState({
      allFilteredPortfolios: portfolios,
      locationFilter: '',
    });
    this.resetFilter(portfolios);
  };

  resetResponsibilityFilter = () => {
    const { locationFilter, allPortfolios } = this.state;

    let portfolios;
    if (locationFilter) {
      portfolios = this.filter(locationFilter, allPortfolios, 'location');
    } else {
      portfolios = allPortfolios;
    }
    this.setState({
      allFilteredPortfolios: portfolios,
      responsibilityFilter: '',
    });
    this.resetFilter(portfolios);
  };

  render() {
    const { children } = this.props;
    const {
      visiblePortfolios: portfolios,
      hiddenContent,
      locations,
      responsibilities,
    } = this.state;
    const {
      showContent,
      setLocationFilter,
      setResponsibilityFilter,
      resetLocationFilter,
      resetResponsibilityFilter,
    } = this;

    return (
      <PortfolioContext.Provider
        value={{
          portfolios,
          hiddenContent,
          locations,
          responsibilities,
          showContent,
          setLocationFilter,
          setResponsibilityFilter,
          resetLocationFilter,
          resetResponsibilityFilter,
        }}
      >
        {children}
      </PortfolioContext.Provider>
    );
  }
}
